import { useCallback, useEffect, useRef } from 'react';

import { useAppSelector } from '@/Application/Hooks/Redux';
import { EShippingType } from '@/modules/cart/domain/Cart';
import { SendShippingEvents } from '@/modules/shared/utils/gtmEvents';
import {
  getEcommerceCheckout,
  getEcommerceFromGroup,
  getLeadTimes,
  getProductTotalPrice,
  sendTypeDeliveryPageViewGTMEvent
} from '@/modules/shared/utils/gtmEvents/events';
import { sendEvent } from '@/modules/shared/utils/gtmEvents/sendEvents/sendShippingEvents';
import {
  ShippingEventInteractiveActions,
  ShippingEventInteractiveTags
} from '@/modules/shared/utils/gtmEvents/types';
import {
  EWebViewNameEvent,
  ShippingEventInteractiveCategories
} from '@/modules/shared/utils/gtmEvents/types';

const { ACTION_CHECKOUT_ADD_INFO } = EWebViewNameEvent;
const { ACTION_CHECKOUT_PRODUCTS_NOT_UNAVAILABLE_SHIPPING } = EWebViewNameEvent;

export const useSendShippingEvents = () => {
  const eventSent = useRef<Record<string, boolean>>({});
  const { data: cart } = useAppSelector((state) => state.cart);
  const { data: shippingMethodsData } = useAppSelector(
    (state) => state.shippingMethods
  );

  const sendGtmLeadTimeEvent = useCallback(() => {
    const { ACTION_CHECKOUT_MEASURE_GROUPING_LEADTIME } = EWebViewNameEvent;
    const { CheckoutInfo } = ShippingEventInteractiveCategories;

    cart?.deliveryInfo?.deliveryGroups?.forEach((group, index) => {
      const products = cart?.lineItems?.filter((item) => {
        const sku = item?.variant?.sku ?? '';
        return group?.products?.includes(sku) ?? false;
      });
      const tag = group?.optionSelected?.deliveryDateFrom?.split('T')[0] ?? '';
      const value = getProductTotalPrice(products, cart.productOptions) ?? 0;
      const vendedor =
        group?.shippingOption?.shippingType === EShippingType.DELIVERY
          ? (group?.shippingOption?.originNodeAdmin ?? '')
          : Array.from(
              new Set(products.flatMap((item) => item?.variant?.sellers ?? []))
            ).join('/');
      const { leadTimeDespacho, leadTimeRetiro, leadTimePuntosCopec } =
        getLeadTimes(group);

      const groupPrice = group?.totalPrice?.centAmount ?? 0;
      const costoDeliveryOption = groupPrice ? groupPrice : '';

      const ecommerce = getEcommerceCheckout(cart);
      const filteredEcommerce = getEcommerceFromGroup(ecommerce, group);

      SendShippingEvents.sendEvent({
        event: ACTION_CHECKOUT_MEASURE_GROUPING_LEADTIME,
        extraFields: {
          'event-interactive-category': CheckoutInfo,
          'event-interactive-accion': group?.products?.join(
            '/'
          ) as ShippingEventInteractiveActions,
          'event-interactive-tag': tag,
          'event-interactive-value': value,
          'event-interactive-leadtime_despacho': leadTimeDespacho ?? '',
          'event-interactive-leadtime_retiro': leadTimeRetiro ?? '',
          'event-interactive-leadtime_puntoscopec': leadTimePuntosCopec ?? '',
          dimension46: costoDeliveryOption,
          dimension47: `${index + 1}-${cart?.deliveryInfo?.deliveryGroups?.length ?? 0}`,
          'event-interactive-vendedor': vendedor,
          ecommerce: filteredEcommerce
        }
      });
    });
  }, [cart]);

  const sendContinueWithPaymentEvent = () => {
    const { CheckoutSummary } = ShippingEventInteractiveCategories;
    const { ContinueWithPayment } = ShippingEventInteractiveTags;

    if (cart) {
      const checkout = getEcommerceCheckout(cart);
      SendShippingEvents.sendEvent({
        event: ACTION_CHECKOUT_ADD_INFO,
        extraFields: {
          'event-interactive-category': CheckoutSummary,
          'event-interactive-tag': ContinueWithPayment,
          ecommerce: checkout
        }
      });
    }
  };

  const sendGtmGroupEvent = () => {
    const { ShippingMethod } = ShippingEventInteractiveActions;
    const deliveryGroups = cart?.deliveryInfo?.deliveryGroups;
    if (deliveryGroups) {
      deliveryGroups.forEach((group, index) => {
        SendShippingEvents.sendEvent({
          event: 'baseEvent',
          extraFields: {
            'event-interactive-accion': ShippingMethod,
            'event-interactive-tag': group?.shippingOption?.shippingType ?? '',
            'event-interactive-grupo_despacho': `${index + 1} - ${deliveryGroups.length}`
          }
        });
      });
    }
  };

  const sendContinueGtmEvents = () => {
    sendGtmGroupEvent();
    sendContinueWithPaymentEvent();
    sendGtmLeadTimeEvent();
  };

  useEffect(() => {
    if (!eventSent.current['page-event']) {
      sendEvent({
        event: 'pageEvent'
      });
      eventSent.current['page-event'] = true;
    }
  }, [cart]);

  useEffect(() => {
    if (!eventSent.current['checkout'] && cart?.lineItems?.length) {
      sendTypeDeliveryPageViewGTMEvent(cart);
      eventSent.current['checkout'] = true;
    }
  }, [cart]);

  useEffect(() => {
    if (
      !eventSent.current['checkout-info'] &&
      cart?.deliveryInfo?.deliveryGroups?.length
    ) {
      sendGtmLeadTimeEvent();
      eventSent.current['checkout-info'] = true;
    }
  }, [cart, sendGtmLeadTimeEvent]);

  useEffect(() => {
    const { Checkout } = ShippingEventInteractiveCategories;
    const { Response } = ShippingEventInteractiveActions;

    if (
      !eventSent.current['out-of-stock'] &&
      shippingMethodsData?.unavailable?.length
    ) {
      shippingMethodsData.unavailable.forEach((item, index) => {
        SendShippingEvents.sendEvent({
          event: ACTION_CHECKOUT_PRODUCTS_NOT_UNAVAILABLE_SHIPPING,
          extraFields: {
            'event-interactive-category': Checkout,
            'event-interactive-accion': Response,
            'event-interactive-tag': ShippingEventInteractiveTags.OutOfStock,
            'event-interactive-grupo_despacho': `${index + 1} - ${shippingMethodsData.unavailable.length}`,
            'event-interactive-sku': item.itemId ?? ''
          }
        });
      });
      eventSent.current['out-of-stock'] = true;
    }
  }, [shippingMethodsData?.unavailable]);

  return { sendContinueGtmEvents };
};
